import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileSignature, Loader2, Lock, PenLine, Plus, RefreshCw, Send, ShieldCheck, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { COMPANY } from '@jt/domain';
import { api, ApiError } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { SelectionBar } from '../components/SelectionBar';
import { DataTable, type Column } from '../components/DataTable';
import { TableSkeleton } from '../components/Skeleton';
import { PageNav } from '../components/PageNav';
import { useToast } from '../components/Toast';
import { DocumentEditor } from '../components/DocumentEditor';
import { PrintContractButton, PrintableContract } from '../components/PrintContract';
import { HelpHint } from '../components/HelpHint';
import { SigningSequence } from '../components/SigningSequence';
import { useSession } from '../lib/session';
import type { ContractStanding, ContractSummary } from '../lib/types';

/**
 * The vault.
 *
 * Standing rather than status is what the list shows, because "what is the
 * position of this agreement today" is the question people actually have, and
 * SIGNED answers it wrongly for a contract whose term ended last month.
 */

const kes = (amount: number): string => `KES ${amount.toLocaleString('en-KE')}`;

const onDate = (iso: string | null): string =>
  iso
    ? new Intl.DateTimeFormat('en-KE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Africa/Nairobi',
      }).format(new Date(iso))
    : '—';

/** Words first. Colour repeats what the word already said, never replaces it. */
const STANDING: Record<ContractStanding, { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: 'bg-muted text-muted-foreground' },
  awaiting_company: { label: 'Waiting for us to sign', tone: 'bg-primary-50 text-primary-700' },
  awaiting_customer: { label: 'Waiting for the customer', tone: 'bg-accent-50 text-accent-700' },
  not_yet_started: { label: 'Signed — starts later', tone: 'bg-secondary-50 text-secondary-700' },
  in_force: { label: 'In force', tone: 'bg-secondary-50 text-secondary-700' },
  expired: { label: 'Term ended', tone: 'bg-muted text-muted-foreground' },
  voided: { label: 'Cancelled', tone: 'bg-danger-50 text-danger-700' },
  superseded: { label: 'Replaced', tone: 'bg-muted text-muted-foreground' },
};

export function ContractsPage() {
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { can } = useSession();

  const contracts = useQuery({
    queryKey: ['contracts', status, q, page],
    queryFn: () => api.contracts({ status: status || undefined, q: q || undefined, page }),
    placeholderData: (previous) => previous,
  });

  /* Any change to the filter restarts at page one — page nine of the old
     result is an empty table under a pager that disagrees with it. */
  const filter = (change: () => void) => {
    change();
    setPage(1);
  };

  const columns: Column<ContractSummary>[] = [
    {
      key: 'customer',
      header: 'Customer',
      cell: (contract) => (
        <div className="min-w-0">
          <p className="font-medium">{contract.customerName}</p>
          <p className="text-xs text-muted-foreground">
            {contract.reference}
            {contract.version > 1 && ` · v${contract.version}`}
          </p>
        </div>
      ),
    },
    {
      key: 'package',
      header: 'Package',
      secondary: true,
      cell: (contract) => (
        <span>
          {contract.packageName}
          {contract.speedMbps ? (
            <span className="ml-1 text-xs text-muted-foreground">{contract.speedMbps} Mbps</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'monthly',
      header: 'Monthly',
      numeric: true,
      secondary: true,
      cell: (contract) => kes(contract.monthlyFeeKes),
    },
    {
      key: 'term',
      header: 'Term',
      numeric: true,
      secondary: true,
      cell: (contract) => `${contract.termMonths} mo`,
    },
    {
      key: 'standing',
      header: 'Standing',
      cell: (contract) => {
        const standing = STANDING[contract.standing];
        return (
          <span className="inline-flex items-center gap-1.5">
            {contract.locked && contract.status === 'SIGNED' && (
              <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Locked in the vault" />
            )}
            <span className={`badge ${standing.tone}`}>{standing.label}</span>
          </span>
        );
      },
    },
    {
      key: 'when',
      header: 'When',
      secondary: true,
      cell: (contract) =>
        contract.signedAt
          ? `Signed ${onDate(contract.signedAt)}`
          : `Raised ${onDate(contract.effectiveFrom)}`,
    },
  ];

  const renewals = useQuery({
    queryKey: ['contracts', 'renewals'],
    queryFn: () => api.contractRenewals(60),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            Contracts
            <HelpHint>
              A signed contract is never edited. Correcting one raises a new version that the
              customer signs again, and the original stays readable with its own signatures —
              which is what makes it worth anything as evidence.
            </HelpHint>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every agreement, signed or not. Signed ones are locked and kept for seven years.
          </p>
        </div>
        {can('contract:draft') && (
          <Link to="/customers" className="btn-primary">
            <Plus className="h-4 w-4" aria-hidden="true" />
            New contract
          </Link>
        )}
      </div>

      {/* Renewals first: a term ending is the only thing here with a deadline. */}
      {(renewals.data?.contracts.length ?? 0) > 0 && (
        <section className="card border-accent-200 bg-accent-50/30">
          <h3 className="font-heading text-base">Coming up for renewal</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Sixty days out — early enough that they have not spoken to anyone else yet.
          </p>
          <ul className="mt-3 divide-y divide-border">
            {renewals.data?.contracts.map((contract) => (
              <li key={contract.id} className="flex items-baseline justify-between gap-3 py-2">
                <Link
                  to={`/contracts/${contract.id}`}
                  className="truncate text-sm font-medium text-primary-600 hover:underline"
                >
                  {contract.customerName}
                </Link>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {contract.daysRemaining !== null && contract.daysRemaining >= 0
                    ? `${contract.daysRemaining} days left`
                    : 'Term ended'}{' '}
                  · {contract.packageName}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          className="input max-w-xs"
          placeholder="Customer, reference or title"
          value={q}
          onChange={(event) => filter(() => setQ(event.target.value))}
        />
        <select
          className="input max-w-48"
          value={status}
          onChange={(event) => filter(() => setStatus(event.target.value))}
        >
          <option value="">Every contract</option>
          <option value="DRAFT">Drafts</option>
          <option value="AWAITING_COMPANY">Waiting for us to sign</option>
          <option value="AWAITING_CUSTOMER">Waiting for the customer</option>
          <option value="SIGNED">Signed</option>
          <option value="VOIDED">Cancelled</option>
          <option value="SUPERSEDED">Replaced</option>
        </select>
      </div>

      {contracts.isPending ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : (
        <>
        {/*
          Export only, deliberately. A contract is a signed instrument: there
          is no bulk operation on one that is not either a legal act or a
          mistake, and both belong on the contract itself where the rules are.
        */}
        <SelectionBar
          rows={contracts.data?.contracts ?? []}
          selected={selected}
          onClear={() => setSelected(new Set())}
          rowKey={(contract) => contract.id}
          exportName="contracts"
          csv={[
            { header: 'Reference', value: (c) => c.reference },
            { header: 'Customer', value: (c) => c.customerName },
            { header: 'Title', value: (c) => c.title },
            { header: 'Status', value: (c) => c.status },
            { header: 'Standing', value: (c) => c.standing },
            { header: 'Monthly (KES)', value: (c) => c.monthlyFeeKes },
            { header: 'Term (months)', value: (c) => c.termMonths },
            { header: 'Starts', value: (c) => c.effectiveFrom },
          ]}
        />

        <DataTable
          selected={selected}
          onSelect={setSelected}
          rows={contracts.data?.contracts ?? []}
          columns={columns}
          rowKey={(contract) => contract.id}
          onRowClick={(contract) => navigate(`/contracts/${contract.id}`)}
          caption="Contracts"
          page={
            contracts.data
              ? {
                  page: contracts.data.page,
                  perPage: contracts.data.perPage,
                  total: contracts.data.total,
                  pages: contracts.data.pages,
                }
              : undefined
          }
          onPage={setPage}
          empty={
            <EmptyState
              icon={FileSignature}
              headline={status || q ? 'No contracts match that' : 'No contracts yet'}
              body={
                status || q
                  ? 'Try a shorter search, or clear the filter to see the whole vault.'
                  : 'Agreements appear here once you raise one. Start from a customer, pick their package, and send it for signature.'
              }
              action={status || q ? undefined : { label: 'Choose a customer', to: '/customers' }}
            />
          }
        />
        </>
      )}
    </div>
  );
}

/* ── One contract ────────────────────────────────────────────────────────── */

export function ContractDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [reason, setReason] = useState('');
  const [action, setAction] = useState<'amend' | 'void' | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: '',
    body: '',
    monthlyFeeKes: '',
    installFeeKes: '',
    depositKes: '',
    termMonths: '',
    speedMbps: '',
    effectiveFrom: '',
  });
  const toast = useToast();
  const navigate = useNavigate();

  const contract = useQuery({
    queryKey: ['contract', id],
    queryFn: () => api.contract(id),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['contract', id] });

  /*
   * Editable while nothing has been sent — a draft, or one waiting for the
   * company's signature. `contract:draft` is the capability that raises them,
   * so it is the one that corrects them.
   */
  const mayEdit =
    can('contract:draft') &&
    (contract.data?.status === 'DRAFT' || contract.data?.status === 'AWAITING_COMPANY');

  /** Seeded from what is on the screen, so the form opens on today's figures. */
  const startEditing = () => {
    const it = contract.data;
    if (!it) return;
    setDraft({
      title: it.title,
      body: it.body,
      monthlyFeeKes: String(it.monthlyFeeKes),
      installFeeKes: String(it.installFeeKes),
      depositKes: String(it.depositKes ?? 0),
      termMonths: String(it.termMonths),
      speedMbps: it.speedMbps == null ? '' : String(it.speedMbps),
      effectiveFrom: String(it.effectiveFrom).slice(0, 10),
    });
    setEditing(true);
  };

  /*
   * Putting it up for signature — the moment the wording stops being editable.
   *
   * Nothing leaves the building here, which is the change worth saying out
   * loud in the toast: somebody who presses this expecting an email to go out
   * needs to know it did not, and what is waiting on whom instead.
   */
  /*
   * Saving the terms.
   *
   * Numbers are sent as numbers and an empty speed as null, because the form
   * holds everything as strings — posting "2499" where a number is expected is
   * how a monthly fee ends up as text nobody can sum.
   */
  const edit = useMutation({
    mutationFn: () =>
      api.updateContract(id, {
        title: draft.title.trim(),
        /*
         * No `body`. The wording belongs to the editor on the document, and
         * sending it from here too would let a figure saved after a wording
         * change quietly put the old words back — the form having seeded its
         * copy before the change was made.
         */
        monthlyFeeKes: Number(draft.monthlyFeeKes),
        installFeeKes: Number(draft.installFeeKes),
        depositKes: Number(draft.depositKes),
        termMonths: Number(draft.termMonths),
        speedMbps: draft.speedMbps.trim() === '' ? null : Number(draft.speedMbps),
        effectiveFrom: draft.effectiveFrom,
      }),
    onSuccess: () => {
      setEditing(false);
      refresh();
      toast.done('Saved. Nothing has gone to the customer yet.');
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  /*
   * Saving the contract's wording.
   *
   * The same PATCH the terms form uses, with only the body — so a change to
   * the words cannot carry a stale figure along with it.
   */
  const editBody = useMutation({
    mutationFn: (body: string) => api.updateContract(id, { body }),
    onSuccess: () => {
      refresh();
      toast.done('Saved. Nothing has gone to the customer yet.');
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const send = useMutation({
    mutationFn: () => api.sendContract(id),
    onSuccess: () => {
      refresh();
      toast.done(
        'The wording is frozen. It is now waiting for a signature on behalf of the company — the customer hears nothing until then.',
      );
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const resend = useMutation({
    mutationFn: () => api.resendContract(id),
    onSuccess: () => {
      refresh();
      toast.done('Sent again. The previous link still works.');
    },
    onError: (error: Error) => toast.failed(error.message),
  });
  const amend = useMutation({
    mutationFn: () => api.amendContract(id, reason),
    onSuccess: () => {
      setAction(null);
      setReason('');
      refresh();
      toast.done('Amended. A new version has been drafted and the old one kept.');
    },
    onError: (error: Error) => toast.failed(error.message),
  });
  const voidIt = useMutation({
    mutationFn: () => api.voidContract(id, reason),
    onSuccess: () => {
      setAction(null);
      setReason('');
      refresh();
      toast.done('Cancelled, with your reason on the record.', {
        label: 'Back to contracts',
        onClick: () => navigate('/contracts'),
      });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  if (contract.isPending) {
    /*
     * A skeleton rather than a spinner, so the page does not change height
     * when the contract lands and nothing below moves under the cursor.
     */
    return <TableSkeleton columns={4} rows={6} label="Loading the contract" />;
  }

  if (contract.isError) {
    return <p className="card text-danger-700">{(contract.error as ApiError).message}</p>;
  }

  const data = contract.data;
  const standing = STANDING[data.standing];
  const busy = send.isPending || resend.isPending || amend.isPending || voidIt.isPending;
  const failure = [send.error, resend.error, amend.error, voidIt.error].find(Boolean) as
    | ApiError
    | undefined;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageNav
        crumbs={[{ label: 'Contracts', to: '/contracts' }, { label: data.reference }]}
        backTo="/contracts"
      />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">{data.title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.reference}
            {data.version > 1 ? ` · version ${data.version}` : ''} · {data.customerName}
          </p>
        </div>
        <span className={`badge ${standing.tone}`}>{standing.label}</span>
      </div>

      {/* Integrity. Silent when intact, loud when not — this cannot fail by
          accident, so a failure means somebody has been in the database. */}
      {!data.integrity.intact && (
        <div className="card border-danger-300 bg-danger-50">
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="mt-0.5 h-5 w-5 shrink-0 text-danger-600"
              aria-hidden="true"
            />
            <div>
              <p className="font-semibold text-danger-700">This contract has been altered</p>
              <p className="mt-1 text-sm">{data.integrity.detail}</p>
            </div>
          </div>
        </div>
      )}

      {failure && (
        <p className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{failure.message}</p>
      )}

      <section className="card">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-heading text-base">Terms</h3>

          {/*
            * Editable until it has been sent to somebody.
            *
            * That is one step later than "draft": `AWAITING_COMPANY` means the
            * wording is settled and it is waiting for a signature on behalf of
            * the business — nobody has signed it, no link exists and no email
            * has gone out. A wrong monthly fee spotted one second after
            * pressing "Put it up for signature" used to cost the whole
            * contract, because the only way back was to cancel and start
            * again.
            *
            * The line is the next step, not this one. Signing for the company
            * issues the customer's link and queues their email together, and
            * from then on the figures are covered by a signature and sitting
            * in somebody else's inbox — so an amendment becomes the only
            * honest correction.
            */}
          {mayEdit && !editing && (
            <button type="button" className="btn-secondary" onClick={startEditing}>
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Edit the terms
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Monthly fee (KES)</span>
                <input
                  className="input w-full tabular-nums"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={draft.monthlyFeeKes}
                  onChange={(event) =>
                    setDraft((old) => ({ ...old, monthlyFeeKes: event.target.value }))
                  }
                />
              </label>

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Installation (KES)</span>
                <input
                  className="input w-full tabular-nums"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={draft.installFeeKes}
                  onChange={(event) =>
                    setDraft((old) => ({ ...old, installFeeKes: event.target.value }))
                  }
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  Zero shows on the contract as “Free”.
                </span>
              </label>

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Deposit (KES)</span>
                <input
                  className="input w-full tabular-nums"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={draft.depositKes}
                  onChange={(event) =>
                    setDraft((old) => ({ ...old, depositKes: event.target.value }))
                  }
                />
              </label>

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Term (months)</span>
                <input
                  className="input w-full tabular-nums"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={draft.termMonths}
                  onChange={(event) =>
                    setDraft((old) => ({ ...old, termMonths: event.target.value }))
                  }
                />
              </label>

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Speed (Mbps)</span>
                <input
                  className="input w-full tabular-nums"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={draft.speedMbps}
                  onChange={(event) =>
                    setDraft((old) => ({ ...old, speedMbps: event.target.value }))
                  }
                  placeholder="—"
                />
              </label>

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Starts</span>
                <input
                  className="input w-full"
                  type="date"
                  value={draft.effectiveFrom}
                  onChange={(event) =>
                    setDraft((old) => ({ ...old, effectiveFrom: event.target.value }))
                  }
                />
              </label>
            </div>

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Title</span>
              <input
                className="input w-full"
                value={draft.title}
                onChange={(event) => setDraft((old) => ({ ...old, title: event.target.value }))}
              />
            </label>

            {/*
              * The wording is not here. It is edited on the document below,
              * which is where the other two kinds of document are edited and
              * where somebody looking at the clauses expects to change them.
              * Two textareas holding the same contract on one page is not a
              * choice, it is a doubt about which one wins.
              */}

            <div className="flex flex-wrap gap-2 border-t border-border pt-3">
              <button
                type="button"
                className="btn-primary"
                disabled={edit.isPending}
                onClick={() => edit.mutate()}
              >
                {edit.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Save the terms
              </button>
              <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
          <Fact label="Package" value={data.packageName} />
          <Fact label="Speed" value={data.speedMbps ? `${data.speedMbps} Mbps` : '—'} />
          <Fact label="Monthly" value={kes(data.monthlyFeeKes)} />
          <Fact
            label="Installation"
            value={data.installFeeKes === 0 ? 'Free' : kes(data.installFeeKes)}
          />
          <Fact label="Term" value={`${data.termMonths} months`} />
          <Fact label="Runs" value={`${onDate(data.effectiveFrom)} → ${onDate(data.effectiveTo)}`} />
          {data.retentionUntil && (
            <Fact label="Held in the vault until" value={onDate(data.retentionUntil)} />
          )}
        </dl>
        )}
      </section>

      <section className="card">
        <h3 className="mb-3 font-heading text-base">Signatures</h3>
        {data.signatures.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody has signed yet. Sending it applies the company signature.
          </p>
        ) : (
          <ul className="space-y-3">
            {data.signatures.map((signature) => (
              <li key={signature.id} className="flex items-start gap-3">
                <ShieldCheck
                  className="mt-0.5 h-4 w-4 shrink-0 text-secondary-600"
                  aria-hidden="true"
                />
                <div className="min-w-0 text-sm">
                  <p className="font-medium">
                    {signature.signerName}
                    {signature.signerTitle ? `, ${signature.signerTitle}` : ''}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {signature.party === 'company' ? 'for GoSpeedNet' : 'the customer'}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {onDate(signature.signedAt)}
                    {signature.ip ? ` · from ${signature.ip}` : ''}
                  </p>
                  <p className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">
                    {signature.documentHash.slice(0, 32)}…
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
        {data.integrity.intact && data.signatures.length > 0 && (
          <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
            {data.integrity.detail}
          </p>
        )}
      </section>

      {/* Version chain, when there is one. */}
      {(data.supersedes || data.supersededBy.length > 0) && (
        <section className="card">
          <h3 className="mb-2 font-heading text-base">Versions</h3>
          <ul className="space-y-1.5 text-sm">
            {data.supersedes && (
              <li>
                Replaces{' '}
                <Link
                  to={`/contracts/${data.supersedes.id}`}
                  className="text-primary-600 hover:underline"
                >
                  {data.supersedes.reference} (v{data.supersedes.version})
                </Link>
              </li>
            )}
            {data.supersededBy.map((later) => (
              <li key={later.id}>
                Replaced by{' '}
                <Link to={`/contracts/${later.id}`} className="text-primary-600 hover:underline">
                  {later.reference} (v{later.version})
                </Link>{' '}
                <span className="text-muted-foreground">— {later.status.toLowerCase()}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        The contract itself, read and edited in the same place — the same
        component the letter of appointment and the building agreement use, so
        "how do I change the wording?" has one answer across all three.

        No `standard` is passed, because a customer contract has no canonical
        text to go back to: it came from whichever template was chosen, and
        offering to "restore" something that was never standard would be a lie.
        Templates are edited under Contract wording.
      */}
      <DocumentEditor
        heading="The document"
        blurb="What the customer will be asked to sign."
        body={data.body}
        values={{}}
        canEdit={mayEdit}
        saving={editBody.isPending}
        onSave={(body) => editBody.mutateAsync(body)}
        note="Nothing has been sent to the customer yet. Once somebody signs for the company this document is emailed to them, and after that an amendment is the only way to change it."
      />

      <div className="print:hidden">
        {/* Anybody who may read the contract may keep a copy of it. */}
        <PrintContractButton />
      </div>

      <PrintableContract
        title={data.title}
        reference={data.reference}
        company="GoSpeedNet"
        body={data.body}
        issuedOn={data.sentAt}
        standing={data.status === 'SIGNED' ? 'Executed by both parties' : null}
        parties={[
          { role: 'The provider', name: COMPANY.legalName },
          {
            role: 'The customer',
            name: data.customerName,
            lines: [data.siteLabel, data.customer?.phoneE164, data.customer?.email],
          },
        ]}
        signatures={(data.signatures ?? []).map((signature) => ({
          party: signature.party,
          name: signature.signerName,
          title: signature.signerTitle,
          signedAt: signature.signedAt,
        }))}
      />

      {/* ── Who signs, and in what order ──────────────────────────────── */}
      {data.status !== 'VOIDED' && data.status !== 'SUPERSEDED' && (
        <section className="card space-y-3 print:hidden">
          <h3 className="font-heading text-base">Signing order</h3>
          {/*
            The same component the employment contracts use, with 'customer'
            as the other side. One drawing of the sequence, so the two kinds of
            contract cannot end up explaining the order differently.
          */}
          <SigningSequence
            signed={(data.signatures ?? []).map((one) =>
              one.party === 'company' ? 'company' : 'counterparty',
            )}
            turn={
              data.status === 'DRAFT' || data.status === 'AWAITING_COMPANY'
                ? 'company'
                : data.status === 'AWAITING_CUSTOMER'
                  ? 'counterparty'
                  : null
            }
            counterparty="customer"
            signatures={(data.signatures ?? []).map((one) => ({
              party: one.party === 'company' ? ('company' as const) : ('counterparty' as const),
              signerName: one.signerName,
              signerTitle: one.signerTitle,
              signedAt: one.signedAt,
            }))}
          />

          {data.status === 'DRAFT' && (
            <p className="rounded-xl bg-muted/40 p-3 text-sm text-muted-foreground">
              Still a draft. Put it up for signature below — the customer hears nothing until
              somebody signs it for the company, and until then the terms can still be edited.
            </p>
          )}

          {data.status === 'AWAITING_COMPANY' && (
            <p className="rounded-xl bg-muted/40 p-3 text-sm text-muted-foreground">
              Waiting for a signature on behalf of the company.{' '}
              <strong>The customer has not seen it and has not been emailed.</strong> The terms can
              still be edited — signing is what sends it, and after that an amendment is the only
              way to change anything.
            </p>
          )}
        </section>
      )}

      {/* ── Signing on behalf of the business ─────────────────────────── */}
      {data.status === 'AWAITING_COMPANY' && (
        <CompanySignature
          id={id}
          defaultName={String(data.variables?.['company.manager'] ?? '')}
          defaultTitle={String(data.variables?.['company.managerTitle'] ?? '')}
          customerEmail={data.customer?.email ?? null}
          allowed={can('contract:sign_company')}
          onSigned={refresh}
        />
      )}

      <section className="card">
        <h3 className="mb-3 font-heading text-base">Actions</h3>
        <div className="flex flex-wrap gap-2">
          {data.status === 'DRAFT' && can('contract:send') && (
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => send.mutate()}
            >
              {send.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
              Put it up for signature
            </button>
          )}

          {data.status === 'AWAITING_CUSTOMER' && can('contract:send') && (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => resend.mutate()}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Send the link again
            </button>
          )}

          {data.status === 'SIGNED' && can('contract:amend') && (
            <button type="button" className="btn-secondary" onClick={() => setAction('amend')}>
              <FileSignature className="h-4 w-4" aria-hidden="true" />
              Raise an amendment
            </button>
          )}

          {data.status !== 'VOIDED' && data.status !== 'SUPERSEDED' && can('contract:void') && (
            <button type="button" className="btn-secondary" onClick={() => setAction('void')}>
              <XCircle className="h-4 w-4" aria-hidden="true" />
              Cancel this contract
            </button>
          )}
        </div>

        {data.status === 'SIGNED' && (
          <p className="mt-3 text-xs text-muted-foreground">
            This document cannot be edited by anybody, including an admin. An amendment raises a
            new version for the customer to sign, and this one stays readable exactly as it is.
          </p>
        )}

        {action && (
          <div className="mt-4 rounded-xl border border-border p-4">
            <label className="block">
              <span className="text-sm font-medium">
                {action === 'amend'
                  ? 'Why is this being amended?'
                  : 'Why is this being cancelled?'}
              </span>
              <textarea
                className="input mt-1 min-h-20"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={
                  action === 'amend'
                    ? 'The agreed installation fee was recorded wrongly.'
                    : 'Customer moved out of our coverage before installation.'
                }
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                This goes on the permanent record.
              </span>
            </label>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="btn-primary"
                disabled={busy || reason.trim().length < 8}
                onClick={() => (action === 'amend' ? amend.mutate() : voidIt.mutate())}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {action === 'amend' ? 'Raise the amendment' : 'Cancel it'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setAction(null);
                  setReason('');
                }}
              >
                Never mind
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium">{value}</dd>
    </div>
  );
}

/**
 * Sign on behalf of the business, and choose who is signing.
 *
 * ## Why the name can be changed here
 *
 * The name in settings is a default, not a fact. The chief executive signs
 * most of these; when they are away, or the branch manager is the right person
 * for a particular customer, the document has to say so. A contract signed
 * "Chief Executive Officer" by somebody who is not the chief executive is
 * worse than one signed by whoever actually signed it, so the name is shown,
 * can be handed to somebody else, and the typed signature is checked against
 * whichever name ends up on the page.
 *
 * The API checks the same thing and refuses independently. This exists so the
 * refusal is rare rather than so it is unnecessary.
 */
function CompanySignature({
  id,
  defaultName,
  defaultTitle,
  customerEmail,
  allowed,
  onSigned,
}: {
  id: string;
  defaultName: string;
  defaultTitle: string;
  customerEmail: string | null;
  allowed: boolean;
  onSigned: () => void;
}) {
  const toast = useToast();

  const [handingOver, setHandingOver] = useState(false);
  const [name, setName] = useState(defaultName);
  const [title, setTitle] = useState(defaultTitle);
  const [typedName, setTypedName] = useState('');

  const sign = useMutation({
    mutationFn: () =>
      api.signContractForCompany(id, {
        typedName,
        signatoryName: name,
        signatoryTitle: title,
      }),
    onSuccess: () => {
      onSigned();
      toast.done(
        customerEmail
          ? `Signed. ${customerEmail} has been sent a link to sign their side.`
          : 'Signed. The customer has been sent a link to sign their side.',
      );
      setTypedName('');
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  if (!allowed) {
    return (
      <section className="card print:hidden">
        <h3 className="font-heading text-base">Waiting for a signature</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          The wording is settled and the customer has not been contacted. Somebody who can commit
          the business has to sign it first — ask an administrator.
        </p>
      </section>
    );
  }

  const matches =
    typedName.trim().toLowerCase().replace(/\s+/g, ' ') ===
    name.trim().toLowerCase().replace(/\s+/g, ' ');

  return (
    <section className="card space-y-4 print:hidden">
      <div>
        <h3 className="font-heading text-base">Sign for the company</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Read the wording above first. Signing sends the customer their link, and the document
          cannot be changed afterwards.
        </p>
      </div>

      <div className="rounded-xl border border-border p-3">
        <p className="text-sm">
          Signing as <strong>{name || '— nobody named —'}</strong>
          {title ? `, ${title}` : ''}
        </p>

        {handingOver ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              Name
              <input
                className="input mt-1 w-full"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Full name"
              />
            </label>
            <label className="text-sm">
              Title
              <input
                className="input mt-1 w-full"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Chief Executive Officer"
              />
            </label>
            <p className="text-xs text-muted-foreground sm:col-span-2">
              This is who the document will say signed it. It is recorded as a substitution, with
              the account that made it.
            </p>
          </div>
        ) : (
          <button
            type="button"
            className="mt-1 text-xs text-primary-600 hover:underline"
            onClick={() => setHandingOver(true)}
          >
            Somebody else is signing
          </button>
        )}
      </div>

      <label className="block text-sm">
        Type the name above to sign
        <input
          className="input signature-ink mt-1 w-full text-2xl"
          value={typedName}
          onChange={(event) => setTypedName(event.target.value)}
          placeholder={name || 'Your full name'}
        />
        <span className="mt-1 block text-xs text-muted-foreground">
          Typing it is the signature. The time, the address it came from and a fingerprint of this
          exact wording are kept with it.
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={!matches || typedName.trim().length < 3 || sign.isPending}
          onClick={() => sign.mutate()}
        >
          {sign.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <FileSignature className="h-4 w-4" aria-hidden="true" />
          )}
          Sign and send to the customer
        </button>
        {typedName.trim().length > 0 && !matches && (
          <span className="text-xs text-accent-700">
            That does not match {name}. Change the name above if somebody else is signing.
          </span>
        )}
      </div>
    </section>
  );
}
